/**
 * src/services/api.js
 * Central API client for the Forge frontend.
 * All calls go through here — no fetch() calls in components.
 */

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/** Where the API lives, without the trailing /api — uploads are served there. */
export const API_ORIGIN = BASE_URL.replace(/\/api\/?$/, "");
/**
 * A stored image path turned into something a browser can actually load.
 *
 * Uploaded pictures are written by the backend and served from its own
 * host (/uploads/...). The website and the admin panel are on different
 * hostnames, so a bare "/uploads/media/x.png" would resolve against THEM
 * and 404. Files that ship with the website stay relative, because those
 * really are served from the site itself.
 */
export const mediaUrl = (p) => {
  if (!p) return "";
  if (/^(https?:)?\/\//i.test(p) || p.startsWith("data:")) return p;
  // Room photos and course thumbnails are stored without the leading slash.
  if (p.startsWith("uploads/")) return `${API_ORIGIN}/${p}`;
  if (p.startsWith("/uploads/")) return `${API_ORIGIN}${p}`;
  return p;
};

/**
 * The sized AVIF and WebP copies the backend makes of every uploaded
 * picture (services/images.service.js, spec 09), as srcset strings — or
 * null for pictures that have none. See components/utils/Picture.js.
 */
const VARIANT_WIDTHS = [400, 800, 1200, 1600];
export const mediaSet = (p) => {
  const m = String(p || "").replace(/^(?!\/)/, "/").match(/^(\/uploads\/[a-z-]+\/[^/]+-o)\.(jpg|png)$/);
  if (!m) return null;
  const base = mediaUrl(m[1]);
  const set = (fmt) => VARIANT_WIDTHS.map((w) => `${base}-w${w}.${fmt} ${w}w`).join(", ");
  return { avif: set("avif"), webp: set("webp") };
};


// ── Auth token helpers ────────────────────────────────────────
export const getToken = () => localStorage.getItem("forge_admin_token");
export const setToken = (token) => localStorage.setItem("forge_admin_token", token);
export const clearToken = () => localStorage.removeItem("forge_admin_token");
export const isLoggedIn = () => !!getToken();

// ── Base fetch wrapper ────────────────────────────────────────
async function request(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Signing in answers 401 for a wrong password or an expired step: show it, don't bounce.
  const signingIn = endpoint === "/auth/login" || endpoint === "/auth/mfa/verify";

  // Handle 401 → force logout
  if (res.status === 401 && !signingIn) {
    // Switched off or signed out everywhere: say so on the sign-in page.
    const why = await res.clone().json().catch(() => ({}));
    try { if (why.code) sessionStorage.setItem("admin_signout_reason", why.message || ""); } catch (_) { /* private mode */ }
    clearToken();
    window.location.href = "/admin/login";
    return;
  }

  const data = await res.json().catch(() => ({}));
  // Two-step is required and not set up yet: everything else waits for it.
  if (res.status === 403 && data.code === "mfa_setup_required" && window.location.pathname !== "/admin/security") {
    window.location.href = "/admin/security";
    return;
  }
  if (!res.ok) throw new Error(data.message || "Request failed");
  return data;
}

// ── Multipart (FormData) requests ─────────────────────────────
async function multipart(endpoint, formData, method = "POST") {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || "Upload failed");
  return data;
}

// ─────────────────────────────────────────────────────────────
// STORIES (community map)
// ─────────────────────────────────────────────────────────────
export const storiesAPI = {
  /** Public — approved stories for the map */
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/stories${qs ? "?" + qs : ""}`);
  },
  /** Public — submit a story */
  submit: (payload) => request("/stories", { method: "POST", body: JSON.stringify(payload) }),
  /** Admin — all stories */
  adminList: (params = {}) => request(`/stories/admin?${new URLSearchParams(params)}`),
  /** Admin — update status */
  updateStatus: (id, status) => request(`/stories/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  /** Admin — delete */
  delete: (id) => request(`/stories/${id}`, { method: "DELETE" }),
};

// ─────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  me: () => request("/auth/me"),
  /** Public — the login screen needs the brand before there's a token. */
  identity: () => request("/auth/identity"),
  logout: () => request("/auth/logout", { method: "POST" }),
  /** The second step of signing in: the code from the app (or a recovery code). */
  verifyTwoStep: (mfaToken, code) => request("/auth/mfa/verify", { method: "POST", body: JSON.stringify({ mfaToken, code }) }),
  twoStep: {
    status:  ()         => request("/auth/mfa"),
    setup:   ()         => request("/auth/mfa/setup", { method: "POST" }),
    enable:  (code)     => request("/auth/mfa/enable", { method: "POST", body: JSON.stringify({ code }) }),
    disable: (password) => request("/auth/mfa/disable", { method: "POST", body: JSON.stringify({ password }) }),
  },
};

// ─────────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────────
export const dashboardAPI = {
  attention: () => request("/dashboard/attention"),
  get: (params = {}) => request(`/dashboard${Object.keys(params).length ? `?${new URLSearchParams(params)}` : ""}`),
};

// ─────────────────────────────────────────────────────────────
// APPLICATIONS
// ─────────────────────────────────────────────────────────────
export const applicationsAPI = {
  /** Public — submit application with optional file uploads */
  submit: (formData) => multipart("/applications", formData),

  /** Admin — list with filters */
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/applications?${qs}`);
  },

  /** Admin — get one */
  getOne: (id) => request(`/applications/${id}`),

  /** Admin — update status / notes */
  update: (id, payload) => request(`/applications/${id}`, { method: "PUT", body: JSON.stringify(payload) }),

  /** Admin — add note */
  addNote: (id, note) => request(`/applications/${id}/notes`, { method: "POST", body: JSON.stringify({ note }) }),

  /** Admin — document URL with auth token for direct browser navigation */
  documentUrl: (appId, docId) => {
    const token = getToken();
    const base = `${BASE_URL}/applications/${appId}/documents/${docId}`;
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  },

  /** Admin — broadcast email to applicants */
  broadcast: (payload) => request("/applications/broadcast", { method: "POST", body: JSON.stringify(payload) }),
};

// ─────────────────────────────────────────────────────────────
// SUBSCRIBERS
// ─────────────────────────────────────────────────────────────
export const subscribersAPI = {
  /** Public — subscribe */
  subscribe: (email, source) => request("/subscribers", { method: "POST", body: JSON.stringify({ email, source }) }),

  /** Admin — list */
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/subscribers?${qs}`);
  },

  /** Admin — get one */
  getOne: (id) => request(`/subscribers/${id}`),

  /** Admin — mark unsubscribed */
  unsubscribe: (id) => request(`/subscribers/${id}/unsubscribe`, { method: "PATCH" }),

  /** Admin — delete */
  delete: (id) => request(`/subscribers/${id}`, { method: "DELETE" }),

  /** Admin — broadcast email */
  broadcast: (payload) => request("/subscribers/broadcast", { method: "POST", body: JSON.stringify(payload) }),

  /** Admin — download CSV with auth header (browser-safe blob approach) */
  exportCsv: async (ids = []) => {
    const token = getToken();
    const url = ids.length > 0 ? `${BASE_URL}/subscribers/export?ids=${ids.join(",")}` : `${BASE_URL}/subscribers/export`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Export failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `subscribers_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },
  /** Legacy — kept for backward compat */
  exportUrl: () => `${BASE_URL}/subscribers/export`,
};

// ─────────────────────────────────────────────────────────────
// DONATIONS
// ─────────────────────────────────────────────────────────────
export const donationsAPI = {
  /** Public — check if donations are enabled + stripe configured */
  getStatus: () => request("/donations/status"),

  /** Public — create Stripe PaymentIntent */
  createIntent: (payload) => request("/donations/create-intent", { method: "POST", body: JSON.stringify(payload) }),

  /** Public — confirm donation after Stripe payment succeeds */
  confirmDonation: (payload) => request("/donations/confirm", { method: "POST", body: JSON.stringify(payload) }),

  /** Public — legacy/manual create donation */
  create: (payload) => request("/donations", { method: "POST", body: JSON.stringify(payload) }),

  /** Admin — list */
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/donations?${qs}`);
  },

  /** Admin — get one */
  getOne: (id) => request(`/donations/${id}`),

  /** Admin — update status */
  updateStatus: (id, status) => request(`/donations/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),

  /** Admin — download CSV with auth header */
  exportCsv: async (ids = []) => {
    const token = getToken();
    const url = ids.length > 0 ? `${BASE_URL}/donations/export?ids=${ids.join(",")}` : `${BASE_URL}/donations/export`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Export failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `donations_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },
  exportUrl: () => `${BASE_URL}/donations/export`,
};

// ─────────────────────────────────────────────────────────────
// NOTIFICATIONS
// ─────────────────────────────────────────────────────────────
export const notificationsAPI = {
  list: (params = {}) => request(`/notifications?${new URLSearchParams(params)}`),
  markRead: (id) => request(`/notifications/${id}/read`, { method: "PUT" }),
  markAllRead: () => request("/notifications/read-all", { method: "PUT" }),
  delete: (id) => request(`/notifications/${id}`, { method: "DELETE" }),
};

// ─────────────────────────────────────────────────────────────
// SETTINGS
// ─────────────────────────────────────────────────────────────
export const settingsAPI = {
  get: () => request("/settings"),
  update: (payload) => request("/settings", { method: "PUT", body: JSON.stringify(payload) }),
  /** Send a test email using current SMTP settings */
  testEmail: (to) => request("/settings/test-email", { method: "POST", body: JSON.stringify({ to }) }),
};

// ─────────────────────────────────────────────────────────────
// RETREAT BLOCKS
// ─────────────────────────────────────────────────────────────
export const retreatBlocksAPI = {
  /** params: { scope: "active"|"past"|"all", status, search } */
  list:   (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ).toString();
    return request(`/retreat-blocks${qs ? `?${qs}` : ""}`);
  },
  getOne: (id)            => request(`/retreat-blocks/${id}`),
  create: (payload)       => request("/retreat-blocks", { method:"POST", body:JSON.stringify(payload) }),
  update: (id, payload)   => request(`/retreat-blocks/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  remove: (id)            => request(`/retreat-blocks/${id}`, { method:"DELETE" }),
  complete: (id)          => request(`/retreat-blocks/${id}/complete`, { method:"POST" }),
  /** Rooms for the block from the property's shared inventory (Forge). */
  property: {
    overview: (id, params = {}) => request(`/retreat-blocks/${id}/property${qs(params)}`),
    reserve:  (id, payload)     => request(`/retreat-blocks/${id}/property`, { method:"POST", body:JSON.stringify(payload) }),
    confirm:  (id, ref)         => request(`/retreat-blocks/${id}/property/${encodeURIComponent(ref)}/confirm`, { method:"POST" }),
    release:  (id, ref)         => request(`/retreat-blocks/${id}/property/${encodeURIComponent(ref)}`, { method:"DELETE" }),
  },
};

// ─────────────────────────────────────────────────────────────
// QUEUE
// ─────────────────────────────────────────────────────────────
export const queueAPI = {
  list:          ()                              => request("/queue"),
  eligible:      ()                              => request("/queue/eligible"),
  sendOffer:     (id, retreatBlockId, roomTypeId, expiresAt) => request(`/queue/${id}/offer`, { method:"POST", body:JSON.stringify({ retreat_block_id: retreatBlockId, room_type_id: roomTypeId || null, expires_at: expiresAt || null }) }),
  activateBatch: (applicantIds, expiresAt)        => request("/queue/activate-batch", { method:"POST", body:JSON.stringify({ applicant_ids: applicantIds, expires_at: expiresAt || null }) }),
  sendReminder:  (id)                             => request(`/queue/${id}/remind`, { method:"POST" }),
  withdrawOffer: (id)                             => request(`/queue/${id}/withdraw-offer`, { method:"POST" }),
  remove:        (id)                             => request(`/queue/${id}/remove`, { method:"POST" }),
  requeue:       (id)                             => request(`/queue/${id}/re-queue`, { method:"POST" }),
};

// ─────────────────────────────────────────────────────────────
// PAYMENTS
// ─────────────────────────────────────────────────────────────
export const paymentsAPI = {
  list:         (applicationId)              => request(`/payments/${applicationId}`),
  create:       (applicationId, payload)     => request(`/payments/${applicationId}`, { method:"POST", body:JSON.stringify(payload) }),
  remove:       (applicationId, paymentId)   => request(`/payments/${applicationId}/${paymentId}`, { method:"DELETE" }),
  setTotalOwed: (applicationId, totalOwed)   => request(`/payments/${applicationId}/total-owed`, { method:"PUT", body:JSON.stringify({ total_owed: totalOwed }) }),
};

// ─────────────────────────────────────────────────────────────
// ROOM TYPES
// ─────────────────────────────────────────────────────────────
export const roomTypesAPI = {
  list:   (all=false) => request(`/room-types${all ? "?all=1" : ""}`),
  create: (payload)   => request("/room-types", { method:"POST", body:JSON.stringify(payload) }),
  update: (id, payload) => request(`/room-types/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  remove: (id)         => request(`/room-types/${id}`, { method:"DELETE" }),
  uploadPhotos: (id, files) => {
    const fd = new FormData();
    files.forEach(f => fd.append("photos", f));
    return multipart(`/room-types/${id}/photos`, fd);
  },
  removePhoto: (id, photoId) => request(`/room-types/${id}/photos/${photoId}`, { method:"DELETE" }),
  reorder: (ids) => request("/room-types/order", { method:"PUT", body:JSON.stringify({ ids }) }),
};

// ─────────────────────────────────────────────────────────────
// GALLERIES (V2.1) — room types, rooms, beds and add-ons each have their own
// kind: "room_type" | "room" | "bed" | "activity"
// ─────────────────────────────────────────────────────────────
export const galleryAPI = {
  list:    (kind, ownerId)            => request(`/galleries/${kind}/${ownerId}`),
  upload:  (kind, ownerId, files)     => { const fd = new FormData(); [...files].forEach((f) => fd.append("photos", f)); return multipart(`/galleries/${kind}/${ownerId}`, fd); },
  reorder: (kind, ownerId, ids)       => request(`/galleries/${kind}/${ownerId}/order`, { method:"PUT", body:JSON.stringify({ ids }) }),
  primary: (kind, ownerId, photoId)   => request(`/galleries/${kind}/${ownerId}/${photoId}/primary`, { method:"POST" }),
  replace: (kind, ownerId, photoId, file) => { const fd = new FormData(); fd.append("photo", file); return multipart(`/galleries/${kind}/${ownerId}/${photoId}/replace`, fd); },
  remove:  (kind, ownerId, photoId)   => request(`/galleries/${kind}/${ownerId}/${photoId}`, { method:"DELETE" }),
};

// ─────────────────────────────────────────────────────────────
// COHORTS
// ─────────────────────────────────────────────────────────────
export const cohortsAPI = {
  list:         ()                          => request("/cohorts"),
  getOne:       (blockId)                   => request(`/cohorts/${blockId}`),
  toggleCheckIn:(blockId, applicantId)      => request(`/cohorts/${blockId}/check-in/${applicantId}`, { method:"POST" }),
  addActivity:  (blockId, payload)          => request(`/cohorts/${blockId}/activities`, { method:"POST", body:JSON.stringify(payload) }),
  removeActivity:(blockId, activityId)      => request(`/cohorts/${blockId}/activities/${activityId}`, { method:"DELETE" }),
  broadcast:    (blockId, payload)          => request(`/cohorts/${blockId}/broadcast`, { method:"POST", body:JSON.stringify(payload) }),
};

// ─────────────────────────────────────────────────────────────
// COURSES
// ─────────────────────────────────────────────────────────────
export const coursesAPI = {
  list:    ()             => request("/courses"),
  create:  (payload)      => request("/courses",    { method:"POST",   body:JSON.stringify(payload) }),
  getOne:  (id)           => request(`/courses/${id}`),
  update:  (id, payload)  => request(`/courses/${id}`, { method:"PUT",  body:JSON.stringify(payload) }),
  remove:  (id)           => request(`/courses/${id}`, { method:"DELETE" }),
  uploadThumbnail: (id, file) => {
    const fd = new FormData();
    fd.append("thumbnail", file);
    return multipart(`/courses/${id}/thumbnail`, fd);
  },

  addChapter:    (cid, payload)       => request(`/courses/${cid}/chapters`,         { method:"POST",  body:JSON.stringify(payload) }),
  updateChapter: (cid, chId, payload) => request(`/courses/${cid}/chapters/${chId}`, { method:"PUT",   body:JSON.stringify(payload) }),
  removeChapter: (cid, chId)          => request(`/courses/${cid}/chapters/${chId}`, { method:"DELETE" }),
  sortChapters:  (cid, order)         => request(`/courses/${cid}/chapters/sort`,    { method:"PATCH", body:JSON.stringify({ order }) }),

  addLesson:    (cid, chId, payload)       => request(`/courses/${cid}/chapters/${chId}/lessons`,        { method:"POST",  body:JSON.stringify(payload) }),
  updateLesson: (cid, chId, lid, payload)  => request(`/courses/${cid}/chapters/${chId}/lessons/${lid}`, { method:"PUT",   body:JSON.stringify(payload) }),
  removeLesson: (cid, chId, lid)           => request(`/courses/${cid}/chapters/${chId}/lessons/${lid}`, { method:"DELETE" }),
  sortLessons:  (cid, chId, order)         => request(`/courses/${cid}/chapters/${chId}/lessons/sort`,   { method:"PATCH", body:JSON.stringify({ order }) }),
};

// ─────────────────────────────────────────────────────────────
// RETREAT COHORTS (ceremony groups within a retreat block)
// ─────────────────────────────────────────────────────────────
export const retreatCohortsAPI = {
  list:   (blockId)                    => request(`/retreat-blocks/${blockId}/cohorts`),
  create: (blockId, payload)           => request(`/retreat-blocks/${blockId}/cohorts`, { method:"POST", body:JSON.stringify(payload) }),
  update: (blockId, cohortId, payload) => request(`/retreat-blocks/${blockId}/cohorts/${cohortId}`, { method:"PUT", body:JSON.stringify(payload) }),
  remove: (blockId, cohortId)          => request(`/retreat-blocks/${blockId}/cohorts/${cohortId}`, { method:"DELETE" }),
  assign: (blockId, applicationIds, cohortId) =>
    request(`/retreat-blocks/${blockId}/cohorts/assign`, {
      method: "POST",
      body: JSON.stringify({ application_ids: applicationIds, cohort_id: cohortId ?? null }),
    }),
  autoAssign: (blockId) => request(`/retreat-blocks/${blockId}/cohorts/auto-assign`, { method:"POST" }),
};

// ─────────────────────────────────────────────────────────────
// MESSAGE BOARD (staff moderation)
// ─────────────────────────────────────────────────────────────
export const boardAdminAPI = {
  overview:       ()              => request("/board-admin/overview"),
  createCategory: (payload)       => request("/board-admin/categories", { method:"POST", body:JSON.stringify(payload) }),
  updateCategory: (id, payload)   => request(`/board-admin/categories/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  removeCategory: (id)            => request(`/board-admin/categories/${id}`, { method:"DELETE" }),
  getPost:        (id)            => request(`/board-admin/posts/${id}`),
  moderatePost:   (id, action)    => request(`/board-admin/posts/${id}`, { method:"PATCH", body:JSON.stringify({ action }) }),
  moderateReply:  (id, action)    => request(`/board-admin/replies/${id}`, { method:"PATCH", body:JSON.stringify({ action }) }),
  resolveReport:  (id, status)    => request(`/board-admin/reports/${id}`, { method:"PATCH", body:JSON.stringify({ status }) }),
};

// ─────────────────────────────────────────────────────────────
// RESOURCE LIBRARY (admin)
// ─────────────────────────────────────────────────────────────
export const resourcesAPI = {
  list:           ()            => request("/resources"),
  create:         (formData)    => multipart("/resources", formData),
  update:         (id, payload) => request(`/resources/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  remove:         (id)          => request(`/resources/${id}`, { method:"DELETE" }),
  createCategory: (payload)     => request("/resources/categories", { method:"POST", body:JSON.stringify(payload) }),
  updateCategory: (id, payload) => request(`/resources/categories/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  removeCategory: (id)          => request(`/resources/categories/${id}`, { method:"DELETE" }),
};

// ─────────────────────────────────────────────────────────────
// ROOMS & BEDS
// ─────────────────────────────────────────────────────────────
export const roomsAPI = {
  list:      ()                  => request("/rooms"),
  create:    (payload)           => request("/rooms", { method:"POST", body:JSON.stringify(payload) }),
  update:    (id, payload)       => request(`/rooms/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  remove:    (id)                => request(`/rooms/${id}`, { method:"DELETE" }),
  addBed:    (roomId, name)      => request(`/rooms/${roomId}/beds`, { method:"POST", body:JSON.stringify({ name }) }),
  removeBed: (roomId, bedId)     => request(`/rooms/${roomId}/beds/${bedId}`, { method:"DELETE" }),
  duplicate: (id)                => request(`/rooms/${id}/duplicate`, { method:"POST" }),
  updateBed: (roomId, bedId, payload) => request(`/rooms/${roomId}/beds/${bedId}`, { method:"PUT", body:JSON.stringify(payload) }),
  reorder:   (roomTypeId, ids)   => request("/rooms/order", { method:"PUT", body:JSON.stringify({ room_type_id: roomTypeId, ids }) }),
  reorderBeds: (roomId, ids)     => request(`/rooms/${roomId}/beds/order`, { method:"PUT", body:JSON.stringify({ ids }) }),

  // Per-retreat rooming board
  board:      (blockId)                    => request(`/retreat-blocks/${blockId}/beds`),
  assign:     (blockId, applicationId, bedId) =>
    request(`/retreat-blocks/${blockId}/beds/assign`, {
      method: "POST",
      body: JSON.stringify({ application_id: applicationId, bed_id: bedId ?? null }),
    }),
  autoAssign: (blockId) => request(`/retreat-blocks/${blockId}/beds/auto-assign`, { method:"POST" }),
};

// ─────────────────────────────────────────────────────────────
// BOOKINGS — pathways, activities, diets, who booked what
// ─────────────────────────────────────────────────────────────
/** Authenticated file download (the CSV export needs the admin token). */
async function download(endpoint, filename) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${endpoint}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || "Download failed");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─────────────────────────────────────────────────────────────
// WORKSHOPS — single-date workshops and evening events
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// MEDIA — pictures for workshops and pathways
// ─────────────────────────────────────────────────────────────
export const mediaAPI = {
  list:   ()         => request("/media"),
  upload: (formData) => multipart("/media", formData),
  remove: (name)     => request(`/media/${encodeURIComponent(name)}`, { method: "DELETE" }),
};

export const workshopsAPI = {
  list:           (params = {})   => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v));
    const qs = q.toString();
    return request(`/workshops${qs ? `?${qs}` : ""}`);
  },
  get:            (id)            => request(`/workshops/${id}`),
  create:         (payload)       => request("/workshops", { method:"POST", body:JSON.stringify(payload) }),
  update:         (id, payload)   => request(`/workshops/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  remove:         (id)            => request(`/workshops/${id}`, { method:"DELETE" }),

  signups:        (id)            => request(`/workshops/${id}/signups`),
  updateSignup:   (id, signupId, status) =>
    request(`/workshops/${id}/signups/${signupId}`, { method:"PATCH", body:JSON.stringify({ status }) }),
  removeSignup:   (id, signupId)  => request(`/workshops/${id}/signups/${signupId}`, { method:"DELETE" }),
  exportCsv:      (id, title)     =>
    download(`/workshops/${id}/signups.csv`,
      `${(title || id).toString().replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-signups.csv`),
};

/* Shop — only mounted on products with the shop feature on. */
export const productsAPI = {
  list:    (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v));
    const qs = q.toString();
    return request(`/products${qs ? `?${qs}` : ""}`);
  },
  create:  (payload)     => request("/products", { method:"POST", body:JSON.stringify(payload) }),
  update:  (id, payload) => request(`/products/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  remove:  (id)          => request(`/products/${id}`, { method:"DELETE" }),
};

export const ordersAPI = {
  list:      (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v));
    const qs = q.toString();
    return request(`/products/orders${qs ? `?${qs}` : ""}`);
  },
  update:    (id, payload) => request(`/products/orders/${id}`, { method:"PATCH", body:JSON.stringify(payload) }),
  exportCsv: ()            => download("/products/orders/export", "shop-orders.csv"),
};

export const bookingsAPI = {
  overview:       (blockId)           => request(`/bookings/blocks/${blockId}`),
  exportCsv:      (blockId, name)     => download(`/bookings/blocks/${blockId}/export`, `bookings-${(name || blockId).toString().replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`),
  createPathway:  (blockId, payload)  => request(`/bookings/blocks/${blockId}/pathways`, { method:"POST", body:JSON.stringify(payload) }),
  copyPathways:   (blockId, fromId)   => request(`/bookings/blocks/${blockId}/pathways/copy`, { method:"POST", body:JSON.stringify({ from_block_id: fromId }) }),
  updatePathway:  (id, payload)       => request(`/bookings/pathways/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  removePathway:  (id)                => request(`/bookings/pathways/${id}`, { method:"DELETE" }),
  setActivities:  (blockId, list)     => request(`/bookings/blocks/${blockId}/activities`, { method:"PUT", body:JSON.stringify({ activities: list }) }),
  update:         (id, payload)       => request(`/bookings/${id}`, { method:"PUT", body:JSON.stringify(payload) }),
  cancel:         (id, payload)       => request(`/bookings/${id}/cancel`, { method:"POST", body:JSON.stringify(payload) }),
  catalog: {
    activities:     ()              => request("/bookings/catalog/activities"),
    saveActivity:   (payload, id)   => request(`/bookings/catalog/activities${id ? `/${id}` : ""}`, { method: id ? "PUT" : "POST", body:JSON.stringify(payload) }),
    removeActivity: (id)            => request(`/bookings/catalog/activities/${id}`, { method:"DELETE" }),
    duplicateActivity: (id)         => request(`/bookings/catalog/activities/${id}/duplicate`, { method:"POST" }),
    reorderActivities: (ids)        => request("/bookings/catalog/activities/order", { method:"PUT", body:JSON.stringify({ ids }) }),
    diets:          ()              => request("/bookings/catalog/diets"),
    saveDiet:       (payload, id)   => request(`/bookings/catalog/diets${id ? `/${id}` : ""}`, { method: id ? "PUT" : "POST", body:JSON.stringify(payload) }),
    removeDiet:     (id)            => request(`/bookings/catalog/diets/${id}`, { method:"DELETE" }),
  },
};

// ─────────────────────────────────────────────────────────────
// COMMUNITY (Discourse) — connection status + group sync
// ─────────────────────────────────────────────────────────────
export const communityAPI = {
  status:  () => request("/community/status"),
  syncAll: () => request("/community/sync", { method:"POST" }),
  overview: (fresh) => request(`/community/overview${fresh ? "?fresh=1" : ""}`),
  setUpGroups: () => request("/community/groups", { method:"POST" }),
  spaces: () => request("/community/spaces"),
  openSpace: (product, ref_id) => request("/community/spaces", { method:"POST", body:JSON.stringify({ product, ref_id }) }),
};

// ─────────────────────────────────────────────────────────────
// MAKUA RESERVATIONS — rooms, reservations, kitchen (spec 01–06)
// ─────────────────────────────────────────────────────────────
const qs = (params = {}) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
  return q ? `?${q}` : "";
};
const json = (method, body) => ({ method, body: JSON.stringify(body || {}) });

/** A CSV download link (the token rides in the query string, as for other exports). */
export const csvUrl = (path, params = {}) => `${BASE_URL}${path}${qs({ ...params, token: getToken() })}`;

export const reservationsAdminAPI = {
  list:        (params)              => request(`/reservations${qs(params)}`),
  get:         (id)                  => request(`/reservations/${id}`),
  create:      (payload)             => request("/reservations", json("POST", payload)),
  update:      (id, payload)         => request(`/reservations/${id}`, json("PUT", payload)),
  updateGuest: (id, guestId, payload)=> request(`/reservations/${id}/guests/${guestId}`, json("PUT", payload)),
  cancel:      (id, reason)          => request(`/reservations/${id}/cancel`, json("POST", { reason })),
  confirm:     (id, payload)         => request(`/reservations/${id}/confirm`, json("POST", payload)),
  hold:        (id, payload)         => request(`/reservations/${id}/hold`, json("POST", payload)),
  pay:         (id, payload)         => request(`/reservations/${id}/payments`, json("POST", payload)),
  reassign:    (id, moves)           => request(`/reservations/${id}/reassign`, json("POST", { moves })),
  updateAddon: (id, addonId, payload)=> request(`/reservations/${id}/addons/${addonId}`, json("PUT", payload)),
  addLine:     (id, payload)         => request(`/reservations/${id}/lines`, json("POST", payload)),
  removeLine:  (id, lineId)          => request(`/reservations/${id}/lines/${lineId}`, { method: "DELETE" }),
  csv:         (params)              => csvUrl("/reservations/export.csv", params),
  quote:       (payload)             => request("/reservations/quote", json("POST", payload)),
  people:      (q)                   => request(`/reservations/people${qs({ q })}`),
  guests:      (params)              => request(`/reservations/guests${qs(params)}`),
  changeDates: (id, payload)         => request(`/reservations/${id}/dates`, json("POST", payload)),
};

export const inventoryAPI = {
  tree:        ()                    => request("/inventory/tree"),
  calendar:    (from, to)            => request(`/inventory/calendar${qs({ from, to })}`),
  free:        (start, end, excludeReservation) => request(`/inventory/free${qs({ start, end, exclude_reservation: excludeReservation })}`),
  block:       (payload)             => request("/inventory/blocks", json("POST", payload)),
  release:     (id)                  => request(`/inventory/blocks/${id}`, { method: "DELETE" }),
  updateBlock: (id, payload)         => request(`/inventory/blocks/${id}`, json("PUT", payload)),
};

export const kitchenAPI = {
  report:      (params)              => request(`/kitchen/report${qs(params)}`),
  csv:         (params)              => csvUrl("/kitchen/report", { ...params, format: "csv" }),
  menus:       (from, to)            => request(`/kitchen/menus${qs({ from, to })}`),
  saveMenu:    (payload)             => request("/kitchen/menus", json("PUT", payload)),
  diets:       ()                    => request("/kitchen/diets"),
  saveDiet:    (payload, id)         => request(`/kitchen/diets${id ? `/${id}` : ""}`, json(id ? "PUT" : "POST", payload)),
  removeDiet:  (id)                  => request(`/kitchen/diets/${id}`, { method: "DELETE" }),
};

export const waitlistAPI = {
  list:        (params)              => request(`/waitlist${qs(params)}`),
  update:      (id, payload)         => request(`/waitlist/${id}`, json("PUT", payload)),
};

/** Third-party sellers (Makua). */
export const sellersAPI = {
  list:        ()                    => request("/sellers"),
  get:         (id)                  => request(`/sellers/${id}`),
  create:      (payload)             => request("/sellers", json("POST", payload)),
  update:      (id, payload)         => request(`/sellers/${id}`, json("PUT", payload)),
  report:      (params)              => csvUrl("/sellers/report.csv", params),
};

/** Shared course library: who gets which course, and how they're doing (both products). */
export const courseAssignmentsAPI = {
  library:     ()                    => request("/course-assignments/library"),
  options:     ()                    => request("/course-assignments/options"),
  list:        (courseId)            => request(`/course-assignments${qs({ course_id: courseId })}`),
  add:         (payload)             => request("/course-assignments", json("POST", payload)),
  remove:      (id)                  => request(`/course-assignments/${id}`, { method: "DELETE" }),
  enrolment:   (courseId)            => request(`/course-assignments/enrolment${qs({ course_id: courseId })}`),
};

export const bookingConfigAPI = {
  get:         ()                    => request("/booking-config"),
  save:        (payload)             => request("/booking-config", json("PUT", payload)),
};

export const retreatTypesAPI = {
  list:        ()                    => request("/retreat-types"),
  save:        (payload, id)         => request(`/retreat-types${id ? `/${id}` : ""}`, json(id ? "PUT" : "POST", payload)),
  remove:      (id)                  => request(`/retreat-types/${id}`, { method: "DELETE" }),
  reorder:     (ids)                 => request("/retreat-types/order", json("PUT", { ids })),
};

export const scheduledAPI = {
  list:        (params)              => request(`/scheduled-retreats${qs(params)}`),
  get:         (id)                  => request(`/scheduled-retreats/${id}`),
  saveCohort:  (id, payload, cohortId)  => request(`/scheduled-retreats/${id}/cohorts${cohortId ? `/${cohortId}` : ""}`, json(cohortId ? "PUT" : "POST", payload)),
  removeCohort:(id, cohortId)        => request(`/scheduled-retreats/${id}/cohorts/${cohortId}`, { method: "DELETE" }),
  saveSession: (id, payload, sessionId) => request(`/scheduled-retreats/${id}/sessions${sessionId ? `/${sessionId}` : ""}`, json(sessionId ? "PUT" : "POST", payload)),
  removeSession:(id, sessionId)      => request(`/scheduled-retreats/${id}/sessions/${sessionId}`, { method: "DELETE" }),
};
